import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/components/AuthProvider";
import DocumentTitle from "@/components/DocumentTitle";
import { ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${API}/api` });

function bearer() {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/**
 * Wave 4 #13 · "your kibanda" dashboard.
 * Manager-only view. Reads the caller's kibanda from GET /api/me/kibanda
 * (returns {organiser, pricing}). Shows events, followers, roses, upcoming
 * slot count, trial days left + tier + next price. No charging yet.
 */
export default function KibandaDashboard() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [followers, setFollowers] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/admin/login?next=/app/kibanda", { replace: true }); return; }
    let alive = true;
    setLoading(true);
    client.get("/me/kibanda", { headers: bearer() })
      .then(async (r) => {
        if (!alive) return;
        setData(r.data);
        const org = r.data?.organiser;
        if (org?.handle) {
          const [ev, fc] = await Promise.all([
            client.get(`/public/organisers/${encodeURIComponent(org.handle)}`).catch(() => ({ data: { events: [] } })),
            client.get(`/follow/counts?kind=organiser&key=${encodeURIComponent(org.handle)}`).catch(() => ({ data: { followers: 0 } })),
          ]);
          if (!alive) return;
          setEvents(ev.data.events || []);
          setFollowers(fc.data.followers || 0);
        }
      })
      .catch(() => setData({ organiser: null }))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [user, authLoading, nav]);

  const org = data?.organiser;
  const pricing = data?.pricing || {};
  const isTrialActive = pricing.state === "trial" && (pricing.trial_days_left || 0) > 0;
  const isLapsed = pricing.state === "trial" && (pricing.trial_days_left || 0) === 0 && !org?.kibanda_first_paid_at;

  const upcomingSlots = events.reduce((n, e) => {
    const list = e.slots_view || [{ start_at: e.start_at }];
    const now = new Date().toISOString();
    return n + list.filter((s) => (s.end_at || s.start_at || "") >= now).length;
  }, 0);

  return (
    <div className="flex h-full flex-col" data-testid="kibanda-dashboard"
         style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
      <DocumentTitle title="your kibanda · watu wako" />
      <div className="flex-1 overflow-y-auto px-5 pt-4 pb-10">
        <div className="mx-auto" style={{ maxWidth: 960 }}>
          <button onClick={() => nav(-1)} data-testid="kibanda-back"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6,
                           background: "transparent", border: 0, color: "var(--ww-text-muted)",
                           padding: "10px 4px", minHeight: 44, cursor: "pointer" }}>
            <ArrowLeft size={16} /> back
          </button>

          <p className="mt-3" style={{ fontFamily: "Arial", textTransform: "uppercase",
                                        letterSpacing: "0.22em", fontSize: 11, color: "var(--ww-text-muted)" }}>
            your kibanda
          </p>
          <h1 className="lowercase" style={{ fontFamily: "Poppins", fontWeight: 800,
                                              letterSpacing: "-0.025em", fontSize: 32, lineHeight: 1.05 }}>
            {loading ? "loading…" : (org?.name || org?.handle || "no kibanda yet")}
          </h1>

          {!loading && !org && (
            <div data-testid="kibanda-none"
                 className="mt-6 rounded-2xl border p-6"
                 style={{ borderColor: "var(--ww-line)", background: "var(--ww-surface)" }}>
              <p style={{ fontFamily: "Arial", fontSize: 14, color: "var(--ww-text-muted)" }}>
                you don't manage a kibanda yet. once the desk hands over a curator to your email, this page will show your trial + tier + pricing rail.
              </p>
            </div>
          )}

          {!loading && org && (
            <>
              <section data-testid="kibanda-pricing"
                       className="mt-6 grid gap-3"
                       style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
                <Stat label="tier" value={pricing.is_founding ? `founding #${pricing.founding_seat_number || org.founding_seat_number || 0}` : "standard"} testId="kibanda-stat-tier" />
                <Stat label="days free left" value={String(pricing.trial_days_left ?? 0)} testId="kibanda-stat-trial" />
                <Stat label="current price (KES/mo)" value={String(pricing.current_price_kes ?? 0)} testId="kibanda-stat-current" />
                <Stat label="next price (KES/mo)" value={String(pricing.next_price_kes ?? 0)} testId="kibanda-stat-next" />
              </section>

              {isTrialActive && (
                <p data-testid="kibanda-trial-banner"
                   className="mt-3 rounded-md border p-3"
                   style={{ borderColor: "var(--ww-line)", background: "var(--ww-surface)",
                            fontFamily: "Arial", fontSize: 13, color: "var(--ww-text)" }}>
                  free kibanda trial · {pricing.trial_days_left} day{pricing.trial_days_left === 1 ? "" : "s"} left · charging waits for m-pesa setup.
                </p>
              )}
              {isLapsed && (
                <p data-testid="kibanda-lapsed-banner"
                   className="mt-3 rounded-md border p-3"
                   style={{ borderColor: "#B32020", background: "var(--ww-surface)",
                            fontFamily: "Arial", fontSize: 13, color: "#FF9AA0" }}>
                  trial ended · your page has reverted to a free listing until payment. no events or posts were deleted.
                </p>
              )}

              <section data-testid="kibanda-metrics"
                       className="mt-6 grid gap-3"
                       style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
                <Stat label="events" value={String(events.length)} testId="kibanda-stat-events" />
                <Stat label="upcoming slots" value={String(upcomingSlots)} testId="kibanda-stat-slots" />
                <Stat label="followers" value={String(followers)} testId="kibanda-stat-followers" />
                <Stat label="roses average" value={org.score_average ? org.score_average.toFixed(2) : "—"} testId="kibanda-stat-roses" />
              </section>

              <h2 className="mt-8 mb-3 lowercase" style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em", fontSize: 22 }}>
                your public page
              </h2>
              <Link to={`/app/organiser/${org.handle}`} data-testid="kibanda-open-page"
                    style={{ display: "inline-block", padding: "10px 16px", borderRadius: 999,
                             background: "#FFD60A", color: "#1A1A30",
                             fontFamily: "Poppins", fontWeight: 700, fontSize: 13,
                             textDecoration: "none", textTransform: "lowercase" }}>
                open @{org.handle}
              </Link>

              <p className="mt-6 text-xs" style={{ color: "var(--ww-text-muted)", fontFamily: "Arial" }}>
                posts + insights coming as v2. today the kibanda page shows events, socials, and follow.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div data-testid={testId}
         className="rounded-xl border p-4"
         style={{ borderColor: "var(--ww-line)", background: "var(--ww-surface)" }}>
      <p style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.2em",
                  fontSize: 10, color: "var(--ww-text-muted)" }}>
        {label}
      </p>
      <p className="mt-1" style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 22,
                                    color: "var(--ww-text)", textTransform: "lowercase" }}>
        {value}
      </p>
    </div>
  );
}
